import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'what-is-rag', title: '1. What is RAG?' },
  { id: 'architecture-overview', title: '2. Architecture Overview' },
  { id: 'setup-knowledge-base', title: '3. Setting Up Your Knowledge Base' },
  { id: 'optimize-citations', title: '4. Optimizing Citation Quality' },
  { id: 'api-integration', title: '5. API Integration Examples' },
  { id: 'testing-validation', title: '6. Testing & Validation' },
  { id: 'production-checklist', title: '7. Production Deployment' },
];

const BuildRagSystemPage = () => {
  return (
    <>
      <NextSeo
        title="Build a Full RAG System Before Lunch"
        description="Complete guide to building retrieval-augmented generation systems with Chat.co. Learn architecture, setup, optimization, and deployment."
        canonical="https://chat.co/docs/build-rag-system"
        openGraph={{
          url: 'https://chat.co/docs/build-rag-system',
          title: 'Build a Full RAG System Before Lunch | Chat.co',
          description:
            'Complete guide to building retrieval-augmented generation systems with Chat.co. Learn architecture, setup, optimization, and deployment.',
          type: 'article',
          article: {
            publishedTime: '2026-01-01T00:00:00Z',
            modifiedTime: '2026-01-26T00:00:00Z',
            section: 'Tutorials',
            tags: ['RAG', 'retrieval augmented generation', 'AI architecture', 'LLM'],
          },
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co - Build a RAG System',
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />

      <DocsArticleLayout
        category="Tutorial"
        categoryColor="text-blue-700"
        title="Build a Full RAG System Before Lunch"
        description="A comprehensive guide to building retrieval-augmented generation systems that deliver accurate, cited answers from your knowledge base."
        readingTime="25 min read"
        lastUpdated="January 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Getting Started Guide',
          href: PageLink.docsGettingStarted,
        }}
        nextArticle={{
          title: 'API Documentation',
          href: PageLink.docsApiReference,
        }}
        keyTakeaways={[
          'RAG combines retrieval and generation for accurate, grounded answers',
          'Chat.co handles the complex infrastructure—you focus on content',
          'Citations let users verify answers against source documents',
          'API integration available for Node.js, Python, and REST',
          'Test thoroughly before production deployment',
        ]}>
        {/* Introduction */}
        <p className="lead text-lg text-gray-700">
          Retrieval-Augmented Generation (RAG) is the secret sauce behind modern AI assistants that
          can answer questions accurately using your specific data. In this tutorial, you&apos;ll
          build a production-ready RAG system from scratch.
        </p>

        <div className="my-8 rounded-xl border border-blue-200 bg-blue-50 p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
              <Icon icon="tabler:target" className="text-xl text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900">What you&apos;ll learn</h4>
              <ul className="mt-2 space-y-1 text-blue-800">
                <li>How RAG works and why it matters for enterprise AI</li>
                <li>Optimal knowledge base structure for retrieval accuracy</li>
                <li>Best practices for citation quality</li>
                <li>API integration patterns (Node.js & Python)</li>
                <li>Production deployment best practices</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 1: What is RAG */}
        <section id="what-is-rag" className="scroll-mt-24">
          <h2>1. What is RAG and Why It Matters</h2>

          <p>
            <strong>Retrieval-Augmented Generation (RAG)</strong> is an AI architecture that
            combines the power of large language models with your organization&apos;s specific
            knowledge. Instead of relying solely on the LLM&apos;s training data, RAG systems:
          </p>

          <ol>
            <li>
              <strong>Retrieve</strong> relevant documents from your knowledge base
            </li>
            <li>
              <strong>Augment</strong> the AI&apos;s context with this retrieved information
            </li>
            <li>
              <strong>Generate</strong> responses grounded in your actual data
            </li>
          </ol>

          <h3>The Problem RAG Solves</h3>
          <p>
            Traditional LLMs have a critical limitation: they can only respond based on their
            training data, which may be outdated or lack your specific domain knowledge. This leads
            to:
          </p>

          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <h4 className="flex items-center gap-2 font-semibold text-red-900">
                <Icon icon="tabler:x" className="text-lg" />
                Without RAG
              </h4>
              <ul className="mt-2 space-y-1 text-sm text-red-800">
                <li>Hallucinated or outdated information</li>
                <li>No citations or verifiable sources</li>
                <li>Generic responses lacking specificity</li>
                <li>Can&apos;t answer about proprietary data</li>
              </ul>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <h4 className="flex items-center gap-2 font-semibold text-green-900">
                <Icon icon="tabler:check" className="text-lg" />
                With RAG
              </h4>
              <ul className="mt-2 space-y-1 text-sm text-green-800">
                <li>Accurate, grounded responses</li>
                <li>Full citation support</li>
                <li>Domain-specific expertise</li>
                <li>Always up-to-date with your data</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 2: Architecture Overview */}
        <section id="architecture-overview" className="scroll-mt-24">
          <h2>2. Architecture Overview</h2>

          <p>
            Chat.co implements RAG using a robust, scalable architecture. Here&apos;s how the
            components work together:
          </p>

          <div className="my-8 overflow-x-auto rounded-xl border border-gray-200 bg-gray-50 p-6">
            <div className="min-w-[600px]">
              {/* Architecture Diagram */}
              <div className="flex items-center justify-between gap-4">
                {/* User */}
                <div className="flex flex-col items-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-gray-200">
                    <Icon icon="tabler:user" className="text-2xl text-gray-600" />
                  </div>
                  <span className="mt-2 text-sm font-medium text-gray-700">User Query</span>
                </div>

                <Icon icon="tabler:arrow-right" className="text-2xl text-gray-400" />

                {/* Embedding */}
                <div className="flex flex-col items-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-blue-100">
                    <Icon icon="tabler:transform" className="text-2xl text-blue-600" />
                  </div>
                  <span className="mt-2 text-sm font-medium text-gray-700">Embedding</span>
                </div>

                <Icon icon="tabler:arrow-right" className="text-2xl text-gray-400" />

                {/* Vector Search */}
                <div className="flex flex-col items-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-violet-100">
                    <Icon icon="tabler:database-search" className="text-2xl text-violet-600" />
                  </div>
                  <span className="mt-2 text-sm font-medium text-gray-700">Vector Search</span>
                </div>

                <Icon icon="tabler:arrow-right" className="text-2xl text-gray-400" />

                {/* LLM */}
                <div className="flex flex-col items-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-green-100">
                    <Icon icon="tabler:brain" className="text-2xl text-green-600" />
                  </div>
                  <span className="mt-2 text-sm font-medium text-gray-700">LLM + Context</span>
                </div>

                <Icon icon="tabler:arrow-right" className="text-2xl text-gray-400" />

                {/* Response */}
                <div className="flex flex-col items-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-amber-100">
                    <Icon icon="tabler:message" className="text-2xl text-amber-600" />
                  </div>
                  <span className="mt-2 text-sm font-medium text-gray-700">Response</span>
                </div>
              </div>
            </div>
          </div>

          <h3>Component Breakdown</h3>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">1. Document Processing Pipeline</h4>
              <p className="mt-1 text-gray-600">
                When you upload documents, they&apos;re chunked into semantic segments, embedded
                using state-of-the-art models, and stored in a vector database for fast retrieval.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">2. Vector Search Engine</h4>
              <p className="mt-1 text-gray-600">
                User queries are embedded and compared against your document embeddings using cosine
                similarity to find the most relevant chunks.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">3. Context Assembly</h4>
              <p className="mt-1 text-gray-600">
                Retrieved chunks are assembled into a coherent context, ranked by relevance, and
                passed to the LLM along with the user&apos;s question.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">4. Response Generation</h4>
              <p className="mt-1 text-gray-600">
                The LLM generates a response grounded in the provided context, with citations
                pointing back to the original source documents.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Setting Up Knowledge Base */}
        <section id="setup-knowledge-base" className="scroll-mt-24">
          <h2>3. Setting Up Your Knowledge Base</h2>

          <p>
            The quality of your RAG system depends heavily on how you structure and prepare your
            knowledge base. Here&apos;s how to set it up for optimal results.
          </p>

          <h3>Document Preparation Best Practices</h3>

          <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="text-sm text-blue-900">
              <strong>Key Principle:</strong> The AI can only be as good as the data you provide.
              Clean, well-structured documents lead to accurate, helpful responses.
            </p>
          </div>

          <h4>Structure Your Content</h4>
          <ul>
            <li>
              <strong>Use clear headings</strong> — H1, H2, H3 structure helps the system understand
              content hierarchy
            </li>
            <li>
              <strong>Keep paragraphs focused</strong> — One topic per paragraph improves retrieval
              accuracy
            </li>
            <li>
              <strong>Include metadata</strong> — Titles, dates, and categories help with context
            </li>
            <li>
              <strong>Avoid scanned PDFs</strong> — Use text-based documents or OCR-processed files
            </li>
          </ul>

          <h4>Organizing by Category</h4>
          <p>Group related documents together for better retrieval:</p>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`knowledge-base/
├── product/
│   ├── features.pdf
│   ├── pricing.pdf
│   └── comparisons.pdf
├── support/
│   ├── faq.pdf
│   ├── troubleshooting.pdf
│   └── getting-started.pdf
└── policies/
    ├── terms-of-service.pdf
    ├── privacy-policy.pdf
    └── refund-policy.pdf`}
            </pre>
          </div>

          <h3>Upload via dashboard</h3>
          <ol>
            <li>
              Open the bot and go to <code>Knowledge Base</code> → <code>Documents</code>
            </li>
            <li>
              Drag your files onto the dropzone — <code>.pdf</code>, <code>.doc</code>,{' '}
              <code>.docx</code>, <code>.xls</code>, <code>.xlsx</code>, <code>.csv</code> and{' '}
              <code>.md</code>, ten at a time
            </li>
            <li>Wait for each row to finish processing</li>
            <li>Spot-check with Test Bot that the new content is retrievable</li>
          </ol>

          <h3>Upload via API</h3>
          <p>
            Programmatic uploads are a three-step flow: ask for a presigned S3 URL, PUT the bytes
            straight to S3, then finalize so Chat.co creates the processing job. The file never
            passes through the Chat.co API, which is what makes large uploads practical.
          </p>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`// Node.js — presign, PUT to S3, finalize
const fs = require('fs');

const API = 'https://api.chat.co/client/v1';
const KEY = process.env.CHATCO_API_KEY;      // sk_live_... with crawl:write
const BOT = process.env.CHATCO_CHATBOT_ID;

const body = fs.readFileSync('document.pdf');

// 1. Reserve storage and get a presigned PUT URL
const presign = await fetch(\`\${API}/chatbots/\${BOT}/documents/presign-upload\`, {
  method: 'POST',
  headers: { Authorization: \`Bearer \${KEY}\`, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    fileName: 'document.pdf',
    fileSizeBytes: body.length,
    mimeType: 'application/pdf',
  }),
}).then((r) => r.json());

// 2. Upload the bytes directly to S3 — Content-Type must match exactly
await fetch(presign.data.put.url, {
  method: 'PUT',
  headers: presign.data.put.headers,
  body,
});

// 3. Finalize — this creates the document job that extracts and indexes
const job = await fetch(\`\${API}/chatbots/\${BOT}/documents/finalize-upload\`, {
  method: 'POST',
  headers: { Authorization: \`Bearer \${KEY}\`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ uploadId: presign.data.upload.id }),
}).then((r) => r.json());

console.log('Processing job:', job.data);`}
            </pre>
          </div>
        </section>

        {/* Section 4: Optimize Citations */}
        <section id="optimize-citations" className="scroll-mt-24">
          <h2>4. Optimizing Citation Quality</h2>

          <p>
            Citations are what make RAG systems trustworthy. They allow users to verify information
            and build confidence in your AI assistant.
          </p>

          <h3>Citation Best Practices</h3>

          <ol>
            <li>
              <strong>Use descriptive document titles</strong>
              <p className="mt-1 text-gray-600">
                Name files clearly: &quot;2024-Product-Pricing-Guide.pdf&quot; is better than
                &quot;doc1.pdf&quot;
              </p>
            </li>
            <li>
              <strong>Include page numbers</strong>
              <p className="mt-1 text-gray-600">
                Chat.co automatically tracks page numbers for PDF citations
              </p>
            </li>
            <li>
              <strong>Structure content with headers</strong>
              <p className="mt-1 text-gray-600">
                Clear section headers improve citation specificity
              </p>
            </li>
            <li>
              <strong>Avoid duplicate content</strong>
              <p className="mt-1 text-gray-600">
                Multiple documents with the same content can confuse citation attribution
              </p>
            </li>
          </ol>

          <div className="my-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
            <p className="text-sm text-amber-900">
              <strong>Pro Tip:</strong> Enable <code>showCitations</code> in your chatbot appearance
              settings to display citations in the chat interface.
            </p>
          </div>
        </section>

        {/* Section 5: API Integration */}
        <section id="api-integration" className="scroll-mt-24">
          <h2>5. API Integration Examples</h2>

          <p>
            Integrate your RAG system into custom applications using our API. Here are examples in
            popular languages.
          </p>

          <h3>Node.js / TypeScript</h3>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`const axios = require('axios');

const API_KEY = 'sk_live_your_api_key';
const BASE_URL = 'https://api.chat.co/client/v1';

const client = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Authorization': \`Bearer \${API_KEY}\`,
    'Content-Type': 'application/json'
  }
});

async function askQuestion(question) {
  // 1. Create conversation
  const { data: conv } = await client.post('/conversations');
  const conversationId = conv.data.conversation.id;

  // 2. Send message and get response
  const { data: response } = await client.post(
    \`/conversations/\${conversationId}/messages\`,
    { message: question }
  );

  // 3. Extract answer and citations
  const { content, citations } = response.data.botResponse;

  return {
    answer: content,
    citations: citations.map(c => ({
      title: c.title,
      url: c.url,
      snippet: c.snippet
    }))
  };
}

// Usage
const result = await askQuestion('What is your return policy?');
console.log('Answer:', result.answer);
console.log('Sources:', result.citations);`}
            </pre>
          </div>

          <h3>Python</h3>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`import requests
from dataclasses import dataclass
from typing import List, Optional

API_KEY = 'sk_live_your_api_key'
BASE_URL = 'https://api.chat.co/client/v1'

@dataclass
class Citation:
    title: str
    url: str
    snippet: str

@dataclass
class RAGResponse:
    answer: str
    citations: List[Citation]

def ask_question(question: str) -> RAGResponse:
    headers = {
        'Authorization': f'Bearer {API_KEY}',
        'Content-Type': 'application/json'
    }

    # Create conversation
    conv_response = requests.post(
        f'{BASE_URL}/conversations',
        headers=headers,
        json={}
    )
    conversation_id = conv_response.json()['data']['conversation']['id']

    # Send message
    msg_response = requests.post(
        f'{BASE_URL}/conversations/{conversation_id}/messages',
        headers=headers,
        json={'message': question}
    )

    data = msg_response.json()['data']['botResponse']

    return RAGResponse(
        answer=data['content'],
        citations=[
            Citation(
                title=c.get('title', ''),
                url=c.get('url', ''),
                snippet=c.get('snippet', '')
            )
            for c in data.get('citations', [])
        ]
    )

# Usage
result = ask_question('What is your return policy?')
print(f'Answer: {result.answer}')
for citation in result.citations:
    print(f'Source: {citation.title}')`}
            </pre>
          </div>

          <h3>Streaming Responses</h3>
          <p>
            For a better user experience, stream responses in real-time. See the{' '}
            <Link href={PageLink.docsApiReference} className="text-primary-600 hover:underline">
              API Documentation
            </Link>{' '}
            for streaming examples.
          </p>
        </section>

        {/* Section 6: Testing */}
        <section id="testing-validation" className="scroll-mt-24">
          <h2>6. Testing & Validation</h2>

          <p>
            Before deploying to production, thoroughly test your RAG system to ensure accuracy and
            reliability.
          </p>

          <h3>Testing Checklist</h3>

          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                <span className="text-sm font-medium text-gray-600">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Known-Answer Testing</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Ask questions where you know the correct answer. Verify the response is accurate
                  and properly cited.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                <span className="text-sm font-medium text-gray-600">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Edge Case Testing</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Test questions outside your knowledge base. The bot should gracefully indicate
                  when it doesn&apos;t have information.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                <span className="text-sm font-medium text-gray-600">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Ambiguous Query Testing</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Test vague questions to see how the system handles disambiguation.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                <span className="text-sm font-medium text-gray-600">4</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Citation Verification</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Verify that citations point to the correct source documents and page numbers.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 7: Production Checklist */}
        <section id="production-checklist" className="scroll-mt-24">
          <h2>7. Production Deployment Checklist</h2>

          <p>Before going live, ensure your RAG system is production-ready with this checklist.</p>

          <div className="my-6 space-y-2">
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-50">
              <input type="checkbox" className="h-5 w-5 rounded border-gray-300 text-primary-600" />
              <span className="text-gray-900">
                All documents uploaded and processed successfully
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-50">
              <input type="checkbox" className="h-5 w-5 rounded border-gray-300 text-primary-600" />
              <span className="text-gray-900">
                Known-answer tests passing with accurate citations
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-50">
              <input type="checkbox" className="h-5 w-5 rounded border-gray-300 text-primary-600" />
              <span className="text-gray-900">Edge cases handled gracefully</span>
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-50">
              <input type="checkbox" className="h-5 w-5 rounded border-gray-300 text-primary-600" />
              <span className="text-gray-900">API integration tested in staging environment</span>
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-50">
              <input type="checkbox" className="h-5 w-5 rounded border-gray-300 text-primary-600" />
              <span className="text-gray-900">Error handling implemented for API failures</span>
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-50">
              <input type="checkbox" className="h-5 w-5 rounded border-gray-300 text-primary-600" />
              <span className="text-gray-900">Rate limiting configured appropriately</span>
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-50">
              <input type="checkbox" className="h-5 w-5 rounded border-gray-300 text-primary-600" />
              <span className="text-gray-900">Monitoring and logging set up</span>
            </label>
          </div>

          <div className="my-8 rounded-xl border border-green-200 bg-green-50 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-xl text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-green-900">Congratulations!</h4>
                <p className="mt-1 text-green-800">
                  You&apos;ve built a production-ready RAG system. Your AI assistant can now provide
                  accurate, cited answers based on your organization&apos;s knowledge.
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <h3>Continue Learning</h3>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <Link
              href={PageLink.docsApiReference}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:terminal"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">API Documentation</h4>
              <p className="mt-1 text-sm text-gray-600">
                Explore the full API reference with all endpoints and parameters.
              </p>
            </Link>
            <Link
              href={PageLink.docsTroubleshooting}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:bug"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Troubleshooting Guide</h4>
              <p className="mt-1 text-sm text-gray-600">
                Learn how to diagnose and fix common issues.
              </p>
            </Link>
          </div>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default BuildRagSystemPage;
